import { Trans, useLingui } from '@lingui/react/macro';

import AlertBar from '@/design_system/AlertBar';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Message from '@/design_system/Message';
import { RadioItemYesNo } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import IconWarning from '@/icons/Warning.svg';
import { computeDuplicateActionNumber, useGetActionName } from '@/models/actionType';
import { ArticleConditionForm, useUpdateArticleCondition } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { unique } from '@/utils/array';
import useViewPort from '@/utils/useViewport';

import { ConditionContextAndPhotos } from './ConditionContextAndPhotos';
import { ConditionQuestionReadonlyAnswers } from './ConditionQuestionReadonlyAnswers';

export const ActionQuestions = ({
  articleConditionForms,
}: {
  articleConditionForms: ArticleConditionForm[];
}) => {
  const { article, view } = useArticleContext();
  const getActionName = useGetActionName();

  const editableArticleConditionForm = articleConditionForms.find(
    (articleConditionForm) => !articleConditionForm.completedAt
  );

  const completedArticleConditionForms = articleConditionForms.filter(
    (articleConditionForm) => !!articleConditionForm.completedAt
  );

  const isEditable = view.qualityControl.editable && !!editableArticleConditionForm;

  const actionsOnCurrentSnapshot = article.snapshot.articleActions.map((action) => {
    const duplicateNumber = computeDuplicateActionNumber(action, article.snapshot.articleActions);

    return {
      ...action,
      name: getActionName({ action }) + (duplicateNumber ? ` #${duplicateNumber}` : ''),
    };
  });

  const actionsOnCompletedForms = completedArticleConditionForms
    .flatMap((articleConditionForm) =>
      articleConditionForm.articleConditions.filter(
        (articleCondition) => !!articleCondition.articleAction
      )
    )
    .map((articleCondition) => ({
      ...articleCondition.articleAction!,
      name: getActionName({ action: articleCondition.articleAction! }),
    }));

  const actionsToDisplay = unique(
    isEditable ? actionsOnCurrentSnapshot : actionsOnCompletedForms,
    'stableId'
  );

  if (!actionsToDisplay.length) {
    return null;
  }

  return (
    <Stack gap="0.5rem">
      <h3 className="label-100-medium">
        <Trans id="requests.articles.quality-control.actions-performed">Actions performed</Trans>
      </h3>
      <Stack gap="1rem">
        {actionsToDisplay.map((action) => (
          <ActionQuestion
            key={action.id}
            action={action}
            articleConditionForms={articleConditionForms}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const ActionQuestion = ({
  action,
  articleConditionForms,
}: {
  action: {
    id: string;
    stableId: string;
    name: string;
  };
  articleConditionForms: ArticleConditionForm[];
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { article, qualityControlFormData, view } = useArticleContext();

  const { mutateAsync: updateArticleCondition, isPending: isUpdateArticleCondition } =
    useUpdateArticleCondition({
      requestId: article.requestId,
      articleId: article.id,
      conditionForm: qualityControlFormData?.conditionForm,
    });

  const actionName = action.name;

  const editableArticleConditionForm = articleConditionForms.find(
    (articleConditionForm) => !articleConditionForm.completedAt
  );

  const completedArticleConditionForms = articleConditionForms.filter(
    (articleConditionForm) => !!articleConditionForm.completedAt
  );

  const isEditable = !!view.qualityControl.editable && !!editableArticleConditionForm;

  const articleConditionForm =
    editableArticleConditionForm ?? completedArticleConditionForms.at(-1);

  const articleCondition = articleConditionForm?.articleConditions.find(
    (articleCondition) => articleCondition.articleAction?.stableId === action.stableId
  );

  const hasAnsweredNo = articleCondition?.answerData?.yesNoRadioValue === 'no';

  const actionsQuestionsErrors = useActionsQuestionErrors({ actionId: action?.id });

  const invalid =
    isEditable &&
    (actionsQuestionsErrors?.noAnswer ||
      actionsQuestionsErrors?.missingContext ||
      actionsQuestionsErrors?.missingPhoto);

  const tooltipText = isEditable
    ? t({
        id: 'requests.articles.quality-control.actions-questions.ko.warning.message.edit',
        message:
          'Answering "No" marks quality control as failed and requires photos and comments to be added',
      })
    : t({
        id: 'requests.articles.quality-control.actions-questions.ko.warning.message.read',
        message: 'Quality control failed due to this answer',
      });

  const selectedAnswer = articleCondition?.answerData?.yesNoRadioValue ?? null;

  const handleChange = (newValue: 'yes' | 'no' | undefined) => {
    const newlySelectedAnswer =
      newValue && articleCondition?.answerData?.yesNoRadioValue !== newValue;

    if (newlySelectedAnswer) {
      updateArticleCondition({
        articleActionId: action.id,
        answers: [{ data: { yesNoRadioValue: newValue } }],
      });
    }
  };

  return (
    <Stack key={action.id} gap="0.25rem">
      <Box padding="0rem">
        <Stack
          gap="0.5rem"
          style={{ padding: '12px', paddingBottom: 0 }}
          ariaLabel={t({
            id: 'requests.articles.quality-control.actions-questions.title',
            message: `Has the action "${actionName}" been done properly?`,
          })}
        >
          <Stack row gap="0.5rem" style={isMobile ? { flexWrap: 'nowrap', gap: '0.25rem' } : {}}>
            {hasAnsweredNo && (!isMobile || (isMobile && !isEditable)) && (
              <Tooltip content={tooltipText} atStart>
                <Button variant="style-less">
                  <IconWarning
                    style={{
                      fontSize: '1rem',
                      color: '#B25E09',
                      marginTop: isMobile ? '2px' : '0',
                    }}
                    className="text-disabled"
                  />
                </Button>
              </Tooltip>
            )}
            <span className="label-100-medium">
              <Trans id="requests.articles.quality-control.actions-questions.title">
                Has the action &quot;{actionName}&quot; been done properly?
              </Trans>
            </span>
          </Stack>
          {isEditable && (
            <RadioItemYesNo
              direction={isMobile ? 'column' : 'row'}
              value={selectedAnswer}
              onChange={handleChange}
              name={actionName}
              isInvalid={actionsQuestionsErrors?.noAnswer}
              disabled={isUpdateArticleCondition}
            />
          )}
          {hasAnsweredNo && isMobile && isEditable && (
            <AlertBar type="warning" size="small" title={tooltipText} />
          )}
        </Stack>
        <ConditionQuestionReadonlyAnswers
          action={action}
          articleConditionForms={articleConditionForms}
        />
        <ConditionContextAndPhotos
          action={action}
          type="quality-control"
          articleConditionForms={articleConditionForms}
        />
      </Box>
      {!!invalid && (
        <Message type="error">
          <Trans id="requests.articles.quality-control.actions-questions.error">
            Please specify the condition of the item
          </Trans>
        </Message>
      )}
    </Stack>
  );
};

export const useActionsQuestionErrors = ({ actionId }: { actionId?: string }) => {
  const { errors } = useArticleContext();
  const actionsQuestionErrors = errors.qualityControl?.actionQuestions?.find(
    ({ id }) => id === actionId
  );

  return actionsQuestionErrors;
};
